import { useState, useEffect } from "react";
import axios from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * StyleSelector - Dropdown for selecting a compiled Drafting Standard
 */
const StyleSelector = ({ value, onChange, disabled = false, className = "" }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/advocate-style/profiles`);
      setProfiles(response.data);
      
      // If no value set and there's a default, select it
      if (!value && response.data.length > 0) {
        const defaultProfile = response.data.find(p => p.is_default);
        if (defaultProfile && onChange) {
          onChange(defaultProfile.id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch style profiles:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`h-10 bg-muted animate-pulse rounded-md ${className}`} />
    );
  }

  if (profiles.length === 0) {
    return null; // Don't show selector if no profiles
  }

  const selectedProfile = profiles.find(p => p.id === value);

  const getHealthLabel = (profile) => {
    if ((profile?.sample_count || 0) >= 15) return "Excellent";
    if ((profile?.sample_count || 0) >= 5) return "Ready";
    return "Needs More Samples";
  };

  return (
    <div className={className} data-testid="style-selector-container">
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Drafting Standard</span>
      </div>
      <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="w-full" data-testid="style-selector">
          <SelectValue placeholder="Select a drafting standard...">
            {selectedProfile ? (
              <div className="flex items-center gap-2">
                <span>{selectedProfile.label}</span>
                {selectedProfile.is_default && (
                  <Star className="h-3 w-3 text-primary fill-primary" />
                )}
              </div>
            ) : (
              "Select a drafting standard..."
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none" data-testid="style-option-none">
            <div className="flex items-center justify-between w-full">
              <span>System Default</span>
              <Badge variant="outline" className="ml-2 text-xs">No DNA</Badge>
            </div>
          </SelectItem>
          {profiles.map((profile) => (
            <SelectItem 
              key={profile.id} 
              value={profile.id}
              data-testid={`style-option-${profile.id}`}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <span>{profile.label}</span>
                  {profile.is_default && (
                    <Star className="h-3 w-3 text-primary fill-primary" />
                  )}
                </div>
                {(profile.sample_count || profile.confidence_score) && (
                  <Badge 
                    variant={(profile.sample_count || 0) >= 5 ? "default" : "secondary"} 
                    className="ml-2 text-xs"
                  >
                    {getHealthLabel(profile)}
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedProfile && (
        <p className="text-xs text-muted-foreground mt-1">
          {(selectedProfile.sample_count || 0)} opinion(s) • Version {selectedProfile.version || 1}
        </p>
      )}
    </div>
  );
};

export default StyleSelector;
